import { ReactNode } from 'react';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  ForecastKpiViewOption,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES,
  OutcomeVariableType
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import {
  ChartTooltipV2,
  KeyShapeOptions,
  PayloadItem
} from '../../../Components/Charts/ChartTooltip/ChartTooltipV2';
import { headerFormatter } from '../../../Components/Charts/ChartUtils';
import { Brand100, Brand25, Brand50, Brand70, Neutral400 } from '../../../utils/colors';
import { parseEpochDate } from '../../../utils/date-utils';
import {
  ForecastDataTimestampRanges,
  formatCpm,
  formatRawBudget,
  formatSpend,
  GAP_KEY,
  HISTORICAL_KEY
} from '../forecasting-constants';
import { formatForecastViewLabel, getCiFormatter, getKpiFormatter } from '../forecasting-utils';

interface TooltipInput {
  timestamp: number;
  payload: readonly Payload[] | undefined;
}
type TooltipRenderer = (input: TooltipInput) => ReactNode;

interface PrimaryOnlyTooltipBuilderInput {
  dataTimestampRanges: ForecastDataTimestampRanges;
  dateGrouping: string;
  fiscalCal: CompanyFiscalCal;
  primaryBudget: number;
  outcomeVar: OutcomeVariableType;
  kpiName: string;
  kpiView: ForecastKpiViewOption;
}

export const buildKpiPrimaryOnlyTooltipRenderer =
  ({
    dataTimestampRanges,
    dateGrouping,
    fiscalCal,
    primaryBudget,
    outcomeVar,
    kpiName,
    kpiView
  }: PrimaryOnlyTooltipBuilderInput): TooltipRenderer =>
  ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;
    const kpiViewSuffix = kpiView === ForecastKpiViewOption.TOTAL ? 'kpi' : 'kpiEfficiency';
    const kpiCIViewSuffix =
      kpiView === ForecastKpiViewOption.TOTAL
        ? 'confidenceInterval'
        : 'efficiencyConfidenceInterval';
    const label = formatForecastViewLabel(outcomeVar, kpiName, kpiView);
    const formatKpi = getKpiFormatter(outcomeVar, kpiView);
    const formatCi = getCiFormatter(outcomeVar, kpiView);

    const kpiPrimaryOnlyTooltipRenderer = ({
      budgetKey,
      kpiColor,
      kpiKeyShape,
      includeConfidenceInterval = false,
      headerSuffix = ''
    }) => {
      const kpiValue = dataPayload[`${budgetKey}-${kpiViewSuffix}`];
      const ciValue = dataPayload[`${budgetKey}-${kpiCIViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label,
          value: formatKpi(kpiValue),
          color: kpiColor,
          keyShape: kpiKeyShape
        }
      ];

      if (includeConfidenceInterval) {
        items.push({
          label: `${label} Confidence Int.`,
          value: formatCi(ciValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE
        });
      }

      return (
        <ChartTooltipV2
          headers={[`${formattedLabel}${headerSuffix}`]}
          items={items}
          decorateLabelsWithColon={false}
        />
      );
    };

    const trainTestTooltipInput = {
      budgetKey: HISTORICAL_KEY,
      kpiColor: Brand25,
      kpiKeyShape: KeyShapeOptions.LINE
    };

    const gapTooltipInput = {
      ...trainTestTooltipInput,
      budgetKey: GAP_KEY,
      keyOpacity: 0.5,
      headerSuffix: ' (Data Pending)'
    };

    const forecastedTooltipInput = {
      budgetKey: primaryBudget,
      kpiColor: Brand70,
      kpiKeyShape: KeyShapeOptions.DASHED_LINE,
      includeConfidenceInterval: true,
      headerSuffix: ' – Forecasted'
    };

    let input;
    const { trainTestTimestampRange, historicalTimestampRange, forecastResultsTimestampRange } =
      dataTimestampRanges;
    if (timestamp >= trainTestTimestampRange.min && timestamp <= trainTestTimestampRange.max) {
      input = trainTestTooltipInput;
    } else if (
      timestamp >= historicalTimestampRange.min &&
      timestamp <= historicalTimestampRange.max
    ) {
      input = gapTooltipInput;
    } else if (
      timestamp >= forecastResultsTimestampRange.min &&
      timestamp <= forecastResultsTimestampRange.max
    ) {
      input = forecastedTooltipInput;
    }

    return input ? kpiPrimaryOnlyTooltipRenderer(input) : null;
  };

export const buildSpendPrimaryOnlyTooltipRenderer =
  ({
    dataTimestampRanges,
    dateGrouping,
    fiscalCal,
    primaryBudget
  }: PrimaryOnlyTooltipBuilderInput): TooltipRenderer =>
  ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;

    const spendPrimaryOnlyTooltipRenderer = ({
      budgetKey,
      spendColor,
      spendKeyShape,
      cpmColor,
      cpmKeyShape,
      keyOpacity = 1,
      headerSuffix = ''
    }) => {
      const spendValue = dataPayload[`${budgetKey}-spend`];
      const cpmValue = dataPayload[`${budgetKey}-cpm`];

      const items: PayloadItem[] = [
        {
          label: 'Spend',
          value: formatSpend(spendValue),
          color: spendColor,
          keyShape: spendKeyShape,
          keyOpacity
        },
        {
          label: 'CPM',
          value: formatCpm(cpmValue),
          color: cpmColor,
          keyShape: cpmKeyShape,
          keyOpacity
        }
      ];
      return (
        <ChartTooltipV2
          headers={[`${formattedLabel}${headerSuffix}`]}
          items={items}
          decorateLabelsWithColon={false}
        />
      );
    };

    const trainTestTooltipInput = {
      budgetKey: HISTORICAL_KEY,
      spendColor: Brand25,
      spendKeyShape: KeyShapeOptions.SQUARE,
      cpmColor: Neutral400,
      cpmKeyShape: KeyShapeOptions.LINE
    };

    const gapTooltipInput = {
      ...trainTestTooltipInput,
      budgetKey: GAP_KEY,
      keyOpacity: 0.5,
      headerSuffix: ' (Data Pending)'
    };

    const forecastedTooltipInput = {
      budgetKey: primaryBudget,
      spendColor: `url(#shadedStripeNeg${Brand50})`,
      spendKeyShape: KeyShapeOptions.FILLED_RECT,
      cpmColor: Brand100,
      cpmKeyShape: KeyShapeOptions.DASHED_LINE,
      headerSuffix: ' – Forecasted'
    };

    let input;
    const { trainTestTimestampRange, historicalTimestampRange, forecastResultsTimestampRange } =
      dataTimestampRanges;
    if (timestamp >= trainTestTimestampRange.min && timestamp <= trainTestTimestampRange.max) {
      input = trainTestTooltipInput;
    } else if (
      timestamp >= historicalTimestampRange.min &&
      timestamp <= historicalTimestampRange.max
    ) {
      input = gapTooltipInput;
    } else if (
      timestamp >= forecastResultsTimestampRange.min &&
      timestamp <= forecastResultsTimestampRange.max
    ) {
      input = forecastedTooltipInput;
    }

    return input ? spendPrimaryOnlyTooltipRenderer(input) : null;
  };

interface ComparisonTooltipBuilderInput {
  dataTimestampRanges: ForecastDataTimestampRanges;
  dateGrouping: string;
  fiscalCal: CompanyFiscalCal;
  selectedBudget: number | undefined;
  visibleBudgets: number[];
  colorMap: Record<string, string>;
  primaryBudget?: number;
}

interface KpiComparisonTooltipBuilderInput extends ComparisonTooltipBuilderInput {
  outcomeVar: OutcomeVariableType;
  kpiName: string;
  kpiView: ForecastKpiViewOption;
}

export const buildKpiComparisonTooltipRenderer =
  ({
    dataTimestampRanges,
    dateGrouping,
    fiscalCal,
    selectedBudget,
    visibleBudgets,
    colorMap,
    outcomeVar,
    kpiName,
    kpiView,
    primaryBudget
  }: KpiComparisonTooltipBuilderInput): TooltipRenderer =>
  ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;
    const kpiViewSuffix = KPI_VIEW_SUFFICES[kpiView];
    const kpiCIViewSuffix = KPI_CI_VIEW_SUFFICES[kpiView];
    const label = formatForecastViewLabel(outcomeVar, kpiName, kpiView);
    const formatKpi = getKpiFormatter(outcomeVar, kpiView);
    const formatCi = getCiFormatter(outcomeVar, kpiView);

    const historicalRenderer = isDataPending => {
      const budgetKey = isDataPending ? GAP_KEY : HISTORICAL_KEY;
      const keyOpacity = isDataPending ? 0.5 : 1;
      const headerLabel = isDataPending ? `${formattedLabel} (Data Pending)` : formattedLabel;
      const kpiValue = dataPayload[`${budgetKey}-${kpiViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label: `${label}, Historical`,
          value: formatKpi(kpiValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
          keyOpacity
        }
      ];
      return (
        <ChartTooltipV2 headers={[headerLabel]} items={items} decorateLabelsWithColon={false} />
      );
    };

    const forecastedDefaultRenderer = () => {
      const items: PayloadItem[] = visibleBudgets.map(budget => {
        const kpiValue = dataPayload[`${budget}-${kpiViewSuffix}`];
        const formattedBudget = formatRawBudget(budget);
        const isPrimary = budget === primaryBudget;
        const formattedBudgetLabel = isPrimary ? `${formattedBudget} (primary)` : formattedBudget;

        return {
          label: formattedBudgetLabel,
          value: formatKpi(kpiValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE
        };
      });

      return (
        <ChartTooltipV2
          headers={[`${formattedLabel} – Forecasted`]}
          items={items}
          subHeaderLabel='Budget'
          subHeaderValue={label}
          decorateLabelsWithColon={false}
        />
      );
    };

    const forecastedSelectedRenderer = () => {
      const budget = selectedBudget as number;
      const kpiValue = dataPayload[`${budget}-${kpiViewSuffix}`];
      const ciValue = dataPayload[`${budget}-${kpiCIViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label,
          value: formatKpi(kpiValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE
        },
        {
          label: `${label} Confidence Int.`,
          value: formatCi(ciValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.SQUARE,
          keyOpacity: 0.25
        }
      ];

      return (
        <ChartTooltipV2
          headers={[`${formattedLabel} – Forecasted`]}
          items={items}
          decorateLabelsWithColon={false}
        />
      );
    };

    const { trainTestTimestampRange, historicalTimestampRange, forecastResultsTimestampRange } =
      dataTimestampRanges;
    if (timestamp >= trainTestTimestampRange.min && timestamp <= trainTestTimestampRange.max) {
      return historicalRenderer(false);
    }
    if (timestamp >= historicalTimestampRange.min && timestamp <= historicalTimestampRange.max) {
      return historicalRenderer(true);
    }
    if (
      timestamp >= forecastResultsTimestampRange.min &&
      timestamp <= forecastResultsTimestampRange.max
    ) {
      return selectedBudget ? forecastedSelectedRenderer() : forecastedDefaultRenderer();
    }

    return null;
  };

export const buildSpendComparisonTooltipRenderer =
  ({
    dataTimestampRanges,
    dateGrouping,
    fiscalCal,
    selectedBudget,
    visibleBudgets,
    colorMap,
    primaryBudget
  }: ComparisonTooltipBuilderInput): TooltipRenderer =>
  ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;

    const historicalRenderer = isDataPending => {
      const budgetKey = isDataPending ? GAP_KEY : HISTORICAL_KEY;
      const keyOpacity = isDataPending ? 0.5 : 1;
      const headerLabel = isDataPending ? `${formattedLabel} (Data Pending)` : formattedLabel;
      const kpiValue = dataPayload[`${budgetKey}-spend`];

      const items: PayloadItem[] = [
        {
          label: 'Spend, Historical',
          value: formatSpend(kpiValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
          keyOpacity
        }
      ];
      return (
        <ChartTooltipV2 headers={[headerLabel]} items={items} decorateLabelsWithColon={false} />
      );
    };

    const forecastedDefaultRenderer = () => {
      const items: PayloadItem[] = visibleBudgets.map(budget => {
        const spendValue = dataPayload[`${budget}-spend`];
        const formattedBudget = formatRawBudget(budget);
        const isPrimary = budget === primaryBudget;
        const formattedBudgetLabel = isPrimary ? `${formattedBudget} (primary)` : formattedBudget;

        return {
          label: formattedBudgetLabel,
          value: formatSpend(spendValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE
        };
      });

      return (
        <ChartTooltipV2
          headers={[`${formattedLabel} – Forecasted`]}
          items={items}
          subHeaderLabel='Budget'
          subHeaderValue='Spend'
          decorateLabelsWithColon={false}
        />
      );
    };

    const forecastedSelectedRenderer = () => {
      const budget = selectedBudget as number;
      const spendValue = dataPayload[`${budget}-spend`];

      const items: PayloadItem[] = [
        {
          label: 'Spend',
          value: formatSpend(spendValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE
        }
      ];

      return (
        <ChartTooltipV2
          headers={[`${formattedLabel} – Forecasted`]}
          items={items}
          decorateLabelsWithColon={false}
        />
      );
    };

    const { trainTestTimestampRange, historicalTimestampRange, forecastResultsTimestampRange } =
      dataTimestampRanges;
    if (timestamp >= trainTestTimestampRange.min && timestamp <= trainTestTimestampRange.max) {
      return historicalRenderer(false);
    }
    if (timestamp >= historicalTimestampRange.min && timestamp <= historicalTimestampRange.max) {
      return historicalRenderer(true);
    }
    if (
      timestamp >= forecastResultsTimestampRange.min &&
      timestamp <= forecastResultsTimestampRange.max
    ) {
      return selectedBudget ? forecastedSelectedRenderer() : forecastedDefaultRenderer();
    }

    return null;
  };
